import { useAchievementTypes, useDispatch, useReduxState } from 'hooks';
import api from 'api';
import {
  DisplayAttachments,
  DisplayFormattedDate,
  ModalButton
} from 'components/common';
import { BoxTableCell } from 'components/common/box';
import { AccessRequests } from 'components/common/accessRequest';
import { AchievementCompletionEditSelf } from '.';

import type { AchievementAccessRequest } from 'shared-types';
import type {
  AccessRequestActions,
  AccessRequestBody,
  AccessRequestHeader
} from 'components/common/accessRequest/AccessRequests';

type AccessRequestData = AchievementAccessRequest['data'];

interface Props {
  supervisorId?: number;
  updateUsers: () => Promise<void>;
}

const AchievementCompletionsAccessRequests = ({
  supervisorId,
  updateUsers
}: Props) => {
  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { achievement }
  } = useReduxState();

  const { hasCompletion, hasExpiration, hasStartedAt } = useAchievementTypes({
    type: achievement?.type
  });

  const handleRequestAction = async () => {
    await updateUsers();

    const achievementsResponse = await api.get(
      `/admin/achievements?id=${achievement.id}&includeAsSupervisor=true`
    );

    dispatch.updatePageState({
      achievement: achievementsResponse.data.achievements[0]
    });
  };

  const Header: AccessRequestHeader = () => (
    <>
      {hasStartedAt ? (
        <BoxTableCell>{messages.commonStarted}</BoxTableCell>
      ) : null}
      {hasCompletion ? (
        <BoxTableCell>{messages.excelCompletionDate}</BoxTableCell>
      ) : null}
      {hasExpiration ? (
        <BoxTableCell>{messages.commonExpirationDate}</BoxTableCell>
      ) : null}
      <BoxTableCell></BoxTableCell>
    </>
  );

  const Body: AccessRequestBody<AccessRequestData> = ({ userData }) => {
    const dateFormat = 'dd.MM.yyyy';

    return (
      <>
        {hasStartedAt ? (
          <BoxTableCell>
            <DisplayFormattedDate
              date={userData.startedAtDate}
              dateFormat={dateFormat}
            />
          </BoxTableCell>
        ) : null}
        {hasCompletion ? (
          <BoxTableCell>
            <DisplayFormattedDate
              date={userData.completionDate}
              dateFormat={dateFormat}
            />
          </BoxTableCell>
        ) : null}
        {hasExpiration ? (
          <BoxTableCell>
            <DisplayFormattedDate
              date={userData.expirationDate}
              dateFormat={dateFormat}
            />
          </BoxTableCell>
        ) : null}
        <BoxTableCell>
          <div style={{ display: 'flex', gap: 10 }}>
            {userData.attachments?.length ? (
              <DisplayAttachments
                attachments={userData.attachments}
                hideTitle={true}
                useModalButtonPropsForTable={true}
              />
            ) : null}

            {userData.comments ? (
              <ModalButton
                aria-label={messages.commonComment}
                data-tooltip={messages.commonComment}
                category="transparent"
                icon={['far', 'comment']}
                iconStyle={{ fontSize: '1.5rem' }}
                modalProps={{
                  closeButton: true,
                  title: messages.commonComment
                }}
                size="small"
              >
                <div>{userData.comments}</div>
              </ModalButton>
            ) : null}
          </div>
        </BoxTableCell>
      </>
    );
  };

  const Actions: AccessRequestActions<AccessRequestData> = ({
    requestId,
    userData,
    update
  }: {
    requestId: number;
    userData: AchievementAccessRequest['data'];
    update: () => Promise<void>;
  }) => (
    <AchievementCompletionEditSelf
      achievement={achievement}
      inDropdown={true}
      initialValues={{ id: requestId, ...userData }}
      updateAchievements={async () => {
        await update();
        await updateUsers();
      }}
    />
  );

  return achievement.userAccessRequireApproval ? (
    <div style={{ marginBottom: 40 }}>
      <AccessRequests
        customTableElements={{ Header, Body, Actions }}
        onRequestAction={handleRequestAction}
        title={messages.commonPendingRequests}
        url={`/admin/achievements/${achievement.id}/request-access`}
        {...(supervisorId
          ? { additionalFilters: `supervisorId=${supervisorId}` }
          : {})}
      />
    </div>
  ) : null;
};

export default AchievementCompletionsAccessRequests;
